import React, { useState } from 'react'
import classNames from 'classnames'
import { IconChevronDown, IconSparkles } from '@tabler/icons-react'

import { REASON_TYPE, REASON_TYPE_LABEL } from './AgenticInsights.constants'
import { CompositionBar } from './CompositionBar'

const HUE_COUNT = 4

const round = (value) => Math.round(value)

const MacroRow = ({ variant, label, pct, count, onOpen }) => {
  const className = classNames('opportunity-card__macro', `opportunity-card__macro--${variant}`, {
    'opportunity-card__macro--static': !onOpen,
  })
  const inner = (
    <>
      <span
        className={classNames(
          'opportunity-card__macro-dot',
          `opportunity-card__macro-dot--${variant}`
        )}
      />
      <span className="opportunity-card__macro-name">{label}</span>
      <span className="opportunity-card__macro-pct">{round(pct)}%</span>
      {count != null && (
        <span className="opportunity-card__macro-count">≈{count.toLocaleString()}/mo</span>
      )}
      {onOpen && <span className="opportunity-card__macro-open">Open ›</span>}
    </>
  )

  if (!onOpen) {
    return <div className={className}>{inner}</div>
  }
  return (
    <button type="button" className={className} onClick={onOpen}>
      {inner}
    </button>
  )
}

const ReasonRow = ({ reason, onOpen }) => (
  <div
    className="opportunity-card__reason"
    role="button"
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onOpen()
      }
    }}
  >
    <div className="opportunity-card__reason-top">
      <span className="opportunity-card__reason-name">
        {reason.name}
        {reason.hasInsight && (
          <span className="opportunity-card__reason-tag">
            <IconSparkles className="icon-svg" />
            Insight
          </span>
        )}
      </span>
      <span className="opportunity-card__reason-pct">
        <b>{round(reason.pctOfConclusive)}%</b> of decided calls
      </span>
    </div>
    <div className="opportunity-card__reason-foot">
      <span className="opportunity-card__reason-vol">
        ≈{reason.monthlyLost.toLocaleString()}/mo
      </span>
      <span className="opportunity-card__reason-open">Open ›</span>
    </div>
  </div>
)

const ReasonGroup = ({ label, reasons, onOpenReason }) => {
  if (reasons.length === 0) {
    return null
  }
  return (
    <div className="opportunity-card__reason-group">
      <div className="opportunity-card__reason-group-head">
        {label}
        <span className="opportunity-card__reason-group-count">{reasons.length}</span>
      </div>
      {reasons.map((reason) => (
        <ReasonRow
          key={reason.insightId}
          reason={reason}
          onOpen={() => onOpenReason(reason.insightId)}
        />
      ))}
    </div>
  )
}

// A single conversion opportunity on the Explore tab: volume headline + a
// composition bar (always visible), expanding to the full breakdown — Converted,
// Didn't convert (reasons grouped by type), and Inconclusive as parallel rows.
export const OpportunityCard = ({ opportunity, hueIndex, onOpenReason, onOpenInconclusive }) => {
  const [expanded, setExpanded] = useState(false)
  const objections = opportunity.reasons.filter((reason) => reason.type === REASON_TYPE.OBJECTION)
  const operational = opportunity.reasons.filter(
    (reason) => reason.type === REASON_TYPE.OPERATIONAL
  )

  return (
    <div className={classNames('opportunity-card', { 'opportunity-card--open': expanded })}>
      <div
        className="opportunity-card__head"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setExpanded((current) => !current)
          }
        }}
      >
        <div className="opportunity-card__label">
          <span
            className={classNames(
              'opportunity-card__chip',
              `opportunity-card__chip--hue-${hueIndex % HUE_COUNT}`
            )}
          />
          <div className="opportunity-card__label-text">
            <div className="opportunity-card__name">{opportunity.label || opportunity.name}</div>
            {opportunity.description && (
              <div className="opportunity-card__def">{opportunity.description}</div>
            )}
          </div>
        </div>
        <div className="opportunity-card__volume">
          <div className="opportunity-card__volume-value">{round(opportunity.volumeSharePct)}%</div>
          <div className="opportunity-card__volume-label">of calls had this opportunity</div>
          <div className="opportunity-card__volume-count">
            ≈{opportunity.monthlyCalls.toLocaleString()}/mo
          </div>
        </div>
      </div>

      <div className="opportunity-card__bar">
        <CompositionBar
          convertedPct={opportunity.convertedPct}
          didntConvertPct={opportunity.didntConvertPct}
          inconclusivePct={opportunity.inconclusivePct}
        />
      </div>

      <div
        className="opportunity-card__foot"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setExpanded((current) => !current)
          }
        }}
      >
        See the details
        <IconChevronDown className="opportunity-card__chevron icon-svg" />
      </div>

      {expanded && (
        <div className="opportunity-card__body">
          <div className="opportunity-card__body-head">How these calls broke down</div>
          <MacroRow
            variant="converted"
            label="Converted"
            pct={opportunity.convertedPct}
            count={opportunity.convertedCalls}
          />
          <MacroRow variant="didnt" label="Didn't convert" pct={opportunity.didntConvertPct} />
          <div className="opportunity-card__subwrap">
            <ReasonGroup
              label={REASON_TYPE_LABEL[REASON_TYPE.OBJECTION]}
              reasons={objections}
              onOpenReason={onOpenReason}
            />
            <ReasonGroup
              label={REASON_TYPE_LABEL[REASON_TYPE.OPERATIONAL]}
              reasons={operational}
              onOpenReason={onOpenReason}
            />
            {opportunity.otherLossPct > 0.5 && (
              <div className="opportunity-card__other">
                Other reasons <b>{round(opportunity.otherLossPct)}%</b>
                <span className="opportunity-card__other-note">
                  A long tail we haven&apos;t called out individually.
                </span>
              </div>
            )}
          </div>
          <MacroRow
            variant="inconclusive"
            label="Inconclusive"
            pct={opportunity.inconclusivePct}
            onOpen={() => onOpenInconclusive(opportunity.name)}
          />
        </div>
      )}
    </div>
  )
}
